/**
 * src/hooks/useOfflineSync.js — live offline sync state for the UI.
 *
 * Surfaces three things the user cares about offline:
 *   - online: are we connected right now
 *   - pendingCount: how many local changes are waiting to sync
 *   - syncing: is the engine draining the outbox right now
 *
 * Subscribes to the sync engine (fires during a drain) + connectivity, and
 * polls the outbox count on a short interval so edits made WHILE offline (which
 * don't go through the engine) still update the badge promptly.
 */

import { useState, useEffect } from 'react';
import { subscribe as subscribeSync } from '../lib/offline/syncEngine';
import { getOnline, subscribe as subscribeNet } from '../lib/offline/networkStatus';
import { count as outboxCount } from '../lib/offline/outbox';

// TICKET-1105: `enabled` false = no outbox reads and no 4s poll. The indicator
// called this hook (rules of hooks) BEFORE its offline-flag gate, so every user
// polled the outbox every 4s — the read that crashed WebKit on iOS.
export default function useOfflineSync({ enabled = true } = {}) {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? getOnline() : true);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let mounted = true;
    const refresh = () => outboxCount().then((c) => { if (mounted) setPendingCount(c); }).catch(() => {});
    refresh();

    const unsubSync = subscribeSync(({ pendingCount: pc, syncing: s }) => {
      if (!mounted) return;
      if (typeof pc === 'number') setPendingCount(pc);
      setSyncing(!!s);
    });
    const unsubNet = subscribeNet((o) => { if (mounted) { setOnline(o); refresh(); } });
    const poll = setInterval(refresh, 4000);

    return () => { mounted = false; unsubSync(); unsubNet(); clearInterval(poll); };
  }, [enabled]);

  return { online, pendingCount, syncing };
}
